import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { contractPrintersApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { CircleNotch, Plus, Printer, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatApiErrorDetail, formatExpiryDateTime } from '../lib/utils';
import { emailErrorMessage, phoneErrorMessage, validateEmailParts, validatePhoneParts } from '../lib/contactValidation';

const EMPTY_FORM = {
  press_name: '',
  contact_person: '',
  email: '',
  phone: '',
  gstn: '',
  address: '',
  city: '',
  state: '',
  pin_code: '',
  area: '',
};

export default function ContractPrintersPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await contractPrintersApi.list();
      setPrinters(res.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load contract printers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    const emailCheck = validateEmailParts(form.email);
    if (!emailCheck.valid) {
      setFormError(emailErrorMessage(emailCheck, t));
      return;
    }
    const phoneCheck = validatePhoneParts(form.phone);
    if (!phoneCheck.valid) {
      setFormError(phoneErrorMessage(phoneCheck, t));
      return;
    }
    setSaving(true);
    try {
      const res = await contractPrintersApi.create({
        ...form,
        email: emailCheck.email,
        phone: phoneCheck.phone,
        gstn: form.gstn.trim().toUpperCase(),
      });
      toast.success(
        res.data?.email_sent
          ? 'Contract printer created — registration email sent'
          : 'Contract printer created (registration email could not be sent)',
      );
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to create printer');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (id) => {
    try {
      const res = await contractPrintersApi.resendEmail(id);
      toast.success(res.data?.email_sent ? 'Registration email sent' : 'Email could not be sent — check SMTP');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to send email');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contract-printers-page">
        <PageHeader
          title="Contract Printers"
          description="Register third-party printing presses for QR label jobs"
          actions={(
            <Button
              onClick={() => { setForm(EMPTY_FORM); setFormError(''); setOpen(true); }}
              data-testid="add-contract-printer-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Add press
            </Button>
          )}
        />

        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
        ) : printers.length === 0 ? (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardContent className="py-16 text-center">
              <Printer className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>No contract printers yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {printers.map((p) => (
              <Card key={p.id} className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{p.press_name}</h3>
                      <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{p.contact_person}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${p.user_id ? 'bg-green-500/15 text-green-600' : 'bg-amber-500/15 text-amber-700'}`}>
                      {p.user_id ? 'Registered' : 'Pending'}
                    </span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{p.email}</p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    {[p.city, p.state, p.pin_code].filter(Boolean).join(', ')}
                  </p>
                  {p.expires_at && (
                    <p className={`text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      Code expires {formatExpiryDateTime(p.expires_at)} IST
                    </p>
                  )}
                  {!p.user_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResend(p.id)}
                      className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                    >
                      <EnvelopeSimple className="w-3.5 h-3.5 mr-1.5" /> Resend registration email
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : ''} max-w-lg max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>Add contract printer</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : ''}>
                Enter the press contact details. A registration QR email will be sent to this address.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 py-2">
              {formError && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{formError}</div>
              )}
              {[
                ['press_name', 'Press name *'],
                ['contact_person', 'Contact person *'],
                ['email', 'Email *'],
                ['phone', 'Phone *'],
                ['gstn', 'GSTIN *'],
                ['address', 'Address *'],
                ['city', 'City *'],
                ['state', 'State *'],
                ['pin_code', 'PIN code *'],
                ['area', 'Area'],
              ].map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <Label className={isDark ? 'text-zinc-400' : ''}>{label}</Label>
                  <Input
                    value={form[key]}
                    onChange={(e) => setField(key, e.target.value)}
                    required={key !== 'area'}
                    type={key === 'email' ? 'email' : 'text'}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}
                    data-testid={`cpr-${key}`}
                  />
                </div>
              ))}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="cpr-save-btn">
                  {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
